import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { DEFAULT_BUY_TOKEN, DEFAULT_SELL_TOKEN } from '@src/configs/defaults'
import { StepNames } from '@src/enums'
import { ITransactionStatus } from '@src/types/transactionStatus'
import { createContextLogger } from '@src/utils/logger'

export const DEFAULT_STEPS: Record<StepNames, { label: string }> = {
    UNSTARTED: { label: 'Swap has not been started' },
    SWITCH_NETWORKS: { label: 'Switching network' },
    APPROVE_TOKENS_PENDING: { label: 'Approving tokens' },
    APPROVE_TOKENS: { label: 'Approve in Wallet' },
    DEPOSIT_WETH_PENDING: { label: 'Wrapping gas' },
    DEPOSIT_WETH: { label: 'Wrap gas' },
    UNWRAP_WETH_PENDING: { label: 'Unwrapping gas' },
    UNWRAP_WETH: { label: 'Unwrap gas' },
    CONFIRM_SWAP: { label: 'Confirm Swap' },
    CONFIRM_SWAP_PENDING: { label: 'Confirming swap' },
    MATCHING: { label: 'Matching order' },
    MATCH_CREATED: { label: 'Match created' },
    MATCH_EXECUTED: { label: 'Match executed' },
    MATCH_TIMED_OUT: { label: 'Match timed out' },

    // Feather Order Steps
    FEATHER_WAITING_FOR_DEPOSIT: { label: 'Waiting for deposit' },
    FEATHER_DEPOSIT_DETECTED: { label: 'Deposit detected' },
    FEATHER_PROCESSING: { label: 'Processing exchange' },
    FEATHER_SENDING_TO_DESTINATION: { label: 'Sending to destination' },
    FEATHER_COMPLETED: { label: 'Exchange completed' },
    FEATHER_EXPIRED: { label: 'Order expired' },
    FEATHER_FAILED: { label: 'Order failed' },
}

const DEFAULT_TRANSACTION_STATUS: ITransactionStatus = {
    sellData: {
        token: DEFAULT_SELL_TOKEN,
        chainId: DEFAULT_SELL_TOKEN.chainId,
        value: BigInt(0),
    },
    buyData: {
        token: DEFAULT_BUY_TOKEN,
        chainId: DEFAULT_BUY_TOKEN.chainId,
        value: BigInt(0),
    },
    exchangeId: '',
    orderId: '',
    currentStep: StepNames.UNSTARTED,
    steps: DEFAULT_STEPS,
    visible: true,
}

const historyLogger = createContextLogger('HistoryStore')

// Define the type for removed orders returned by cleanupDefaultTransactions
type RemovedOrder = { exchangeId: string; orderId: string }

type HistoryStore = {
    history: Record<string, ITransactionStatus>
    updateHistory: (exchangeId: string | null, data: Partial<ITransactionStatus>) => void
    removeHistory: (exchangeId: string) => void
    getOrder: (exchangeId: string) => ITransactionStatus
    cleanupDefaultTransactions: () => RemovedOrder[]
}

// Helper function to convert BigInt values to strings in an object
const convertBigIntToString = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj
    }

    if (typeof obj === 'bigint') {
        return obj.toString()
    }

    if (typeof obj !== 'object') {
        return obj
    }

    if (Array.isArray(obj)) {
        return obj.map(convertBigIntToString)
    }

    const result: Record<string, any> = {}
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[key] = convertBigIntToString(obj[key])
        }
    }

    return result
}

// Helper function to convert string representations of BigInt back to BigInt
const convertStringToBigInt = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj
    }

    if (typeof obj !== 'object') {
        return obj
    }

    if (Array.isArray(obj)) {
        return obj.map(convertStringToBigInt)
    }

    const result: Record<string, any> = {}
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            // Special handling for known BigInt fields
            if ((key === 'value' && obj.token) || key === 'sellData' || key === 'buyData') {
                if (obj[key]?.value && typeof obj[key].value === 'string' && /^\d+$/.test(obj[key].value)) {
                    try {
                        const copy = { ...obj[key] }
                        copy.value = BigInt(obj[key].value)
                        result[key] = copy
                        continue
                    } catch (e) {
                        // If conversion fails, proceed with normal handling
                    }
                }
            }

            result[key] = convertStringToBigInt(obj[key])
        }
    }

    return result
}

export const useHistoryStore = create<HistoryStore>()(
    persist(
        (set, get) => ({
            history: {},

            updateHistory: (exchangeId, data) => {
                set(state => {
                    if (exchangeId === null) {
                        exchangeId = Object.keys(get().history)[0]
                    }
                    const existingHistory = state.history[exchangeId] || DEFAULT_TRANSACTION_STATUS

                    historyLogger.debug('Updating history for exchange', {
                        exchangeId,
                        hasExistingData: !!state.history[exchangeId],
                        updateData: Object.keys(data),
                    })

                    return {
                        history: {
                            ...state.history,
                            [exchangeId]: { ...existingHistory, ...data },
                        },
                    }
                })
            },

            removeHistory: exchangeId => {
                set(state => {
                    // Use proper destructuring without unused variable
                    const copy = { ...state.history }

                    delete copy[exchangeId]
                    return { history: copy }
                })
            },

            getOrder: exchangeId => {
                const state = get()
                const order = state.history[exchangeId] || DEFAULT_TRANSACTION_STATUS

                return order
            },

            // Remove any transactions with default values (0 -> 0)
            // Returns object with removed exchangeIds and orderIds for external tracking cleanup
            cleanupDefaultTransactions: () => {
                const removedOrders: RemovedOrder[] = []

                set(state => {
                    const newHistory = { ...state.history }
                    let removedCount = 0

                    Object.keys(newHistory).forEach(exchangeId => {
                        const tx = newHistory[exchangeId]
                        if (
                            (tx.sellData?.value === BigInt(0) && tx.buyData?.value === BigInt(0)) ||
                            !tx.createdAt ||
                            Date.now() - new Date(tx.createdAt || Date.now()).getTime() > 1000 * 60 * 60 // Remove if older than 60 minutes
                        ) {
                            // Get the orderId from the transaction before removing it
                            const orderId = tx.orderId || ''

                            // Track removals for external cleanup
                            if (orderId) {
                                removedOrders.push({ exchangeId, orderId })
                            }

                            // Remove from history
                            delete newHistory[exchangeId]
                            removedCount++
                        }
                    })

                    if (removedCount > 0) {
                        historyLogger.info(`Cleaned up ${removedCount} default/expired transactions`)
                    }

                    return { history: newHistory }
                })

                return removedOrders
            },
        }),
        {
            name: 'transaction-history-store',
            // Only persist the history data
            partialize: state => {
                // Convert BigInt values to strings before storing
                return {
                    history: convertBigIntToString(state.history),
                }
            },
            // Use built-in storage but with conversion functions
            storage: createJSONStorage(() => localStorage, {
                // No custom serialization needed since we handle BigInt conversion in partialize
                reviver: (key, value) => {
                    // We'll convert the entire state object after it's parsed
                    return value
                },
            }),
            // Convert string values back to BigInt after retrieval
            onRehydrateStorage: () => state => {
                if (state) {
                    // Convert stored string values back to BigInt
                    state.history = convertStringToBigInt(state.history)
                    historyLogger.debug('Successfully rehydrated history store')
                }
            },
        }
    )
)
